import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setCurrentProject } from '../redux/projectsSlice';
import { parseFeatureProjectFromPath } from '../utils/routeUtils';
import Loader from './Loader';
import './SidebarProjectList.css';

/**
 * Renders the sidebar list of projects from Redux store. Each item links to /project/:id.
 * @param {Object} props
 * @param {() => void} [props.onItemClick] - Called when a project is clicked (e.g. close mobile nav).
 * @param {Array} [props.projects] - Optional pre-fetched list; if provided, uses this instead of Redux.
 */
const SidebarProjectList = ({ onItemClick, projects: projectsProp }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { projectId: featureRouteProjectId } = parseFeatureProjectFromPath(location.pathname);
  const { allProjects = [], loading } = useSelector((state) => state.projects);

  // Projects are fetched by parent Sidebar component
  // No need to fetch again here

  // Use provided projects or Redux projects
  const projects = projectsProp !== undefined && projectsProp !== null
    ? (Array.isArray(projectsProp) ? projectsProp : [])
    : allProjects.map((p) => ({
        id: String(p.id),
        name: p.projectName,
        brief: p.brief,
      }));

  // Show loader when fetching projects (only if no projects prop provided)
  if (loading && !projectsProp && projects.length === 0) {
    return (
      <div style={{ padding: '1rem' }}>
        <Loader size="small" text="Loading projects..." />
      </div>
    );
  }

  if (projects.length === 0) return null;

  const handleProjectClick = (project) => {
    // Find the full project data from allProjects
    const fullProject = allProjects.find((p) => String(p.id) === String(project.id));
    
    // Store the selected project in Redux
    if (fullProject) {
      dispatch(setCurrentProject(fullProject));
    }
    
    // Call the optional onItemClick callback
    onItemClick?.();
  };

  return (
    <ul className="sidebar-project-list" aria-label="Projects">
      {projects.map((project) => (
        <li key={project.id}>
          <NavLink
            to={`/project/${project.id}`}
            className={({ isActive }) => {
              const activeFromFeature =
                featureRouteProjectId != null &&
                String(featureRouteProjectId) === String(project.id);
              return `sidebar-project-item ${isActive || activeFromFeature ? 'active' : ''}`;
            }}
            onClick={() => handleProjectClick(project)}
          >
            {project.name}
          </NavLink>
        </li>
      ))}
    </ul>
  );
};

export default SidebarProjectList;
